import {
    addMessage,
    messageBodyAriaLabel,
    messageOnSuccessfulDeletingAttachment,
    showMessageHistory,
} from './ComposeBody.locstring.json';
import ComposeComponentProps from './ComposeComponentProps';
import { ComposeUniqueState } from 'owa-attachment-filepicker';
import { observer } from 'mobx-react';
import { IconButton } from '@fluentui/react/lib/Button';
import { logUsage } from 'owa-analytics';
import { AttachmentFullViewState } from 'owa-attachment-full-data';
import { AttachmentWellView } from 'owa-attachment-well-view';
import getComposeUniqueState from 'owa-mail-compose-actions/lib/utils/getComposeUniqueState';
import { ClientItemId } from 'owa-client-ids';
import { ControlIcons } from 'owa-control-icons';
import Droppable, { DroppableHandle } from 'owa-dnd/lib/components/Droppable';
import { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { LocalFilesDragData } from 'owa-dnd/lib/utils/LocalFilesDragData';
import { NoteItemDragData } from 'owa-dnd/lib/utils/NoteItemDragData';
import CompositeEditor from 'owa-editor/lib/components/CompositeEditor';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import createAttachments from 'owa-mail-compose-actions/lib/utils/createAttachments';
import triggerPolicyTips from 'owa-mail-compose-actions/lib/actions/triggerPolicyTips';
import updateLastFocusedControl from 'owa-addins-editor-plugin/lib/actions/updateLastFocusedControl';
import { getPluginArray } from 'owa-editor-plugins-list';
import upConvertHelper from 'owa-mail-compose-actions/lib/utils/onUpconvertHelper';
import { mailStore } from 'owa-mail-store';
import { getMailboxInfoFromViewState } from '../utils/getMailboxInfoFromViewState';
import { MailListItemPartDragData } from 'owa-mail-types/lib/types/MailListItemPartDragData';
import { MailListRowDragData } from 'owa-mail-types/lib/types/MailListRowDragData';
import { isPolicyTipsEnabled } from 'owa-policy-tips/lib/utils/isPolicyTipsEnabled';
import EventTrigger from 'owa-service/lib/contract/EventTrigger';
import getAttachmentPolicy from 'owa-session-store/lib/utils/getAttachmentPolicy';
import { ComposeSmartPillBlock } from 'owa-smart-pill-compose';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { DarkModeContext } from 'owa-theme';
import { LastFocusedControl } from 'owa-editor-addin-plugin-types/lib/utils/AddinViewState';
import * as React from 'react';
import { setAnnouncedComponentMessage } from 'owa-attachment-well-data/lib/actions/setAnnouncedComponentMessage';
import { LazyAnnounced } from 'owa-controls-announced';
import { focusToEditor } from 'owa-editor';
import { ContentPosition } from 'roosterjs-editor-types';
import insertContentIntoEditor from 'owa-mail-compose-actions/lib/utils/insertContentIntoEditor';
import {
    CreateAttachmentsProperties,
    lazyConvertImageViewAttachmentToInlineAttachment,
} from 'owa-attachment-well-data';
import {
    AttachmentFileType,
    createLocalComputerFiles,
    createMailItemFiles,
} from 'owa-attachment-file-types';
import {
    AttachmentDragData,
    AttachmentDragDataSource,
    LazyComposeDropHint,
} from 'owa-attachment-drag-and-drop';
import { CreateAttachmentHandler } from 'owa-attachment-filepicker';
import { UIEnabledState } from 'owa-mail-compose-store';
import setDockingTriggerPart from 'owa-editor-docking-plugin/lib/actions/setDockingTriggerPart';
import { EditorPlugin } from 'roosterjs-editor-types';
import createComposePlugins from '../utils/createComposePlugins';
import { getCreateClassicAttachmentHandler } from '../utils/getCreateClassicAttachmentHandler';
import isApprovalResponse from '../utils/isApprovalResponse';
import getGetBottomDockingHeightFunc from '../utils/getGetBottomDockingHeightFunc';

const styles = require('./ComposeBody.scss');
const classNames = require('classnames/bind').bind(styles);
const SCROLL_CONTAINER_SELECTOR = '.customScrollBar';

@observer
export default class ComposeBody extends React.Component<ComposeComponentProps, {}> {
    private plugins: EditorPlugin[];

    composeWrapperDivRef: React.RefObject<DroppableHandle> = React.createRef();
    static contextType = DarkModeContext;

    constructor(props: ComposeComponentProps) {
        super(props);
        this.plugins = getPluginArray(createComposePlugins(this.props.viewState, this.props.sxsId));
    }

    componentDidMount() {
        this.setComposeWrapperOnRoosterPlugins();
    }

    componentDidUpdate() {
        this.setComposeWrapperOnRoosterPlugins();
    }

    public trySetFocus = () => {
        focusToEditor(this.props.viewState);
    };

    render() {
        const { viewState } = this.props;
        const isDarkMode = this.context;
        const attachmentWell = viewState.attachmentWell;
        const hasAttachment =
            (attachmentWell.docViewAttachments && attachmentWell.docViewAttachments.length > 0) ||
            (attachmentWell.imageViewAttachments && attachmentWell.imageViewAttachments.length > 0);
        const bodyClassNames = classNames(styles.bodyContainer, {
            hasAttachment: hasAttachment,
            isInlineCompose: viewState.isInlineCompose,
            sticky: viewState.docking?.useStickyDocking,
            darkMode: isDarkMode,
        });

        return (
            <Droppable
                classNames={bodyClassNames}
                ref={this.composeWrapperDivRef}
                dropViewState={viewState.dropViewState}
                onDrop={this.onDrop}
                canDrop={this.canDrop}
                shouldUseCopyAsDropEffect={true}
                shouldIgnoreTransientOnDragLeave={true}>
                {this.renderAttachmentWell()}
                {this.renderEditor(isDarkMode)}
                {isFeatureEnabled('mc-smartPillsCompose') && renderSmartPillBar()}
                {this.renderShowHistoryBar()}
                {this.renderDropHint()}
                {this.renderAnnounced()}
            </Droppable>
        );
    }

    private renderAnnounced(): JSX.Element | undefined {
        const { announcedComponentMessage } = this.props.viewState.attachmentWell;

        if (announcedComponentMessage) {
            return <LazyAnnounced message={`${announcedComponentMessage}`} />;
        } else {
            return undefined;
        }
    }

    private setComposeWrapperOnRoosterPlugins() {
        const div = this.composeWrapperDivRef.current
            ? this.composeWrapperDivRef.current.getDiv()
            : null;

        const { viewState } = this.props;
        if (viewState?.docking) {
            setDockingTriggerPart(viewState.docking, div);
        }
    }

    private renderDropHint(): JSX.Element {
        const { viewState } = this.props;
        const shouldRenderDropHint: boolean =
            viewState && shouldShowComposeDropHint() && !isApprovalResponse(viewState);
        return shouldRenderDropHint ? (
            <LazyComposeDropHint
                editorViewState={viewState}
                dockingViewState={viewState.docking}
                dropViewState={viewState.dropViewState}
                dataTransferType={mailStore.typeOfItemBeingDragged}
                getBottomDockingHeight={getGetBottomDockingHeightFunc(viewState)}
            />
        ) : null;
    }

    private onAttachmentDeleted = () => {
        // This will trigger the Narrator to announce the message
        setAnnouncedComponentMessage(
            this.props.viewState.attachmentWell,
            loc(messageOnSuccessfulDeletingAttachment)
        );

        if (isPolicyTipsEnabled()) {
            // When attachment is deleted, check DLP information.
            triggerPolicyTips(this.props.viewState, EventTrigger.AttachmentRemoved);
        }
    };

    private onAttachmentMovedInline = (attachment: AttachmentFullViewState, contentId: string) => {
        lazyConvertImageViewAttachmentToInlineAttachment.importAndExecute(
            this.props.viewState.attachmentWell,
            attachment,
            contentId
        );
    };

    private renderAttachmentWell(): JSX.Element {
        const { viewState, sxsId, isSxS } = this.props;
        const parentItemId: ClientItemId = {
            ...viewState.itemId,
            mailboxInfo: getMailboxInfoFromViewState(viewState),
        };
        const isDisabled = viewState.uiEnabledState === UIEnabledState.AllDisabled;
        return (
            <AttachmentWellView
                attachmentWellViewState={viewState.attachmentWell}
                parentItemId={parentItemId}
                isDraft={true}
                infoBar={this.props.viewState}
                onAttachmentDeletedCallback={this.onAttachmentDeleted}
                moveAttachmentToInlineHandler={
                    viewState.bodyType == 'HTML' ? this.onAttachmentMovedInline : null
                }
                sxsId={sxsId}
                isSxS={isSxS}
                onLostFocus={this.trySetFocus}
                referenceAttachmentsSupported={this.referenceAttachmentsSupported}
                isDeletionDisabled={isDisabled}
            />
        );
    }

    private renderEditor(isDarkMode: boolean): JSX.Element {
        const { viewState } = this.props;
        const isHtmlEditor = viewState.bodyType == 'HTML';
        const editorClassNames = classNames(styles.editorBody, {
            isInlineCompose: viewState.isInlineCompose,
            hasRibbon: viewState.isRibbonShown,
            plainTextEditor: !isHtmlEditor,
            darkMode: isDarkMode,
        });
        const disabled = viewState.uiEnabledState === UIEnabledState.AllDisabled;

        return (
            <CompositeEditor
                key={viewState.composeId}
                plugins={this.plugins}
                viewState={viewState}
                classNames={editorClassNames}
                onFocus={this.onEditorFocus}
                ariaLabel={loc(messageBodyAriaLabel)}
                isDarkMode={isDarkMode}
                plainTextPlaceholder={loc(addMessage)}
                scrollContainerSelector={SCROLL_CONTAINER_SELECTOR}
                updateOnDispose={true}
                isEditorDisabled={disabled}
            />
        );
    }

    private renderShowHistoryBar(): JSX.Element {
        const { viewState } = this.props;
        return (
            viewState.isInlineCompose && (
                <IconButton
                    title={loc(showMessageHistory)}
                    ariaLabel={loc(showMessageHistory)}
                    onClick={this.onUpConvert}
                    className={styles.showHistoryButton}
                    iconProps={{
                        iconName: ControlIcons.More,
                        styles: {
                            root: styles.showHistoryButtonIcon,
                        },
                    }}
                />
            )
        );
    }

    private onUpConvert = () => {
        upConvertHelper(this.props.viewState, this.props.sxsId);
    };

    private onDrop = async (dragInfo: DragData) => {
        let attachmentFileList = null;
        let shouldShare = false;
        // Attachment sizes will be updated by the server in non-S/MIME mode
        const shouldUseSizes = isSmimeEnabledInViewState(this.props.viewState.smimeViewState);
        switch (dragInfo.itemType) {
            case DraggableItemTypes.LocalFile:
                const filesDragData = dragInfo as LocalFilesDragData;
                attachmentFileList = createLocalComputerFiles(filesDragData.files);
                break;
            case DraggableItemTypes.MailListRow:
            case DraggableItemTypes.MultiMailListConversationRows:
            case DraggableItemTypes.MultiMailListMessageRows:
                const mailListItemsDragData = dragInfo as MailListRowDragData;
                attachmentFileList = createMailItemFiles(
                    mailListItemsDragData.subjects,
                    mailListItemsDragData.latestItemIds,
                    shouldUseSizes ? mailListItemsDragData.sizes : []
                );
                break;
            case DraggableItemTypes.MailListItemPart:
                const mailListItemPartDragData = dragInfo as MailListItemPartDragData;
                attachmentFileList = createMailItemFiles(
                    mailListItemPartDragData.subjects,
                    mailListItemPartDragData.itemIds,
                    shouldUseSizes ? mailListItemsDragData.sizes : []
                );
                break;
            case DraggableItemTypes.Attachment:
                const attachmentDragData = dragInfo as AttachmentDragData;
                attachmentFileList = [attachmentDragData.attachmentFile];
                shouldShare =
                    attachmentDragData.attachmentFile.fileType === AttachmentFileType.Cloud ||
                    attachmentDragData.attachmentFile.fileType ===
                        AttachmentFileType.CloudSuggestion ||
                    attachmentDragData.attachmentFile.fileType ===
                        AttachmentFileType.ContextualSuggestion;
                if (attachmentDragData.dragSource === AttachmentDragDataSource.FilesFolderTree) {
                    logUsage('WVNAttachmentDroppedFromFolder', {}, { isCore: true });
                }
                break;
            case DraggableItemTypes.NoteFeedItem:
                const noteDragData = dragInfo as NoteItemDragData;
                const { viewState } = this.props;
                focusToEditor(viewState);
                insertContentIntoEditor(
                    viewState,
                    noteDragData.htmlContent /* htmlContent */,
                    '' /* plainTextContent */,
                    ContentPosition.SelectionStart /* insertContentPosition */,
                    'Notes' /* changeSource */,
                    false /* dontSetFocusToEditor */
                );
                logUsage('noteFeedItemDragToMail');
                break;
            default:
                break;
        }

        if (attachmentFileList) {
            logUsage('AttachmentDragAndDrop', [dragInfo.itemType]);
            const baseProperties = { isInline: false, isHiddenInline: false };
            const properties: CreateAttachmentsProperties = shouldShare
                ? { ...baseProperties, shouldShare: true }
                : { ...baseProperties, shouldShare: false };
            if (
                dragInfo.itemType === DraggableItemTypes.LocalFile &&
                isFeatureEnabled('doc-dnd-largeFile')
            ) {
                const isInsertLinkFlightEnabled: boolean =
                    isFeatureEnabled('doc-attachInsert-linkIcon') ||
                    isFeatureEnabled('doc-attachInsert-cloudLinkIcon');
                const createAttachmentsHandler: CreateAttachmentHandler = getCreateClassicAttachmentHandler(
                    this.props.viewState,
                    this.props.sxsId,
                    this.composeWrapperDivRef.current
                        ? this.composeWrapperDivRef.current.getDiv().ownerDocument.defaultView
                        : null,
                    isInsertLinkFlightEnabled
                );
                createAttachmentsHandler({
                    files: attachmentFileList,
                    shouldShare: shouldShare,
                    isInline: false,
                    dataProviderType: null,
                });
            } else {
                createAttachments(attachmentFileList, this.props.viewState, properties, null);
            }
        }
    };

    private onEditorFocus = () => {
        updateLastFocusedControl(this.props.viewState.addin, LastFocusedControl.Editor);
    };

    private referenceAttachmentsSupported = () => {
        return getComposeUniqueState(this.props.viewState) === ComposeUniqueState.None;
    };

    private canDrop = (dragInfo: DragData) => {
        // If classic attachments are disabled then we disable drag and drop
        if (!classicAttachmentsEnabled() || isApprovalResponse(this.props.viewState)) {
            return false;
        }
        const itemType = dragInfo.itemType;
        return (
            itemType === DraggableItemTypes.LocalFile ||
            itemType === DraggableItemTypes.MailListItemPart ||
            itemType === DraggableItemTypes.MailListRow ||
            itemType === DraggableItemTypes.MultiMailListConversationRows ||
            itemType === DraggableItemTypes.MultiMailListMessageRows ||
            itemType === DraggableItemTypes.Attachment ||
            itemType === DraggableItemTypes.NoteFeedItem
        );
    };
}

function renderSmartPillBar(): JSX.Element {
    return <ComposeSmartPillBlock />;
}

function classicAttachmentsEnabled() {
    return getAttachmentPolicy().ClassicAttachmentsEnabled;
}

function shouldShowComposeDropHint(): boolean {
    // If classic attachments are disabled then we disable drag and drop
    if (!classicAttachmentsEnabled()) {
        return false;
    }
    // Compose body only allows to drop MailListRow and LocalFile.
    return (
        mailStore.typeOfItemBeingDragged === DraggableItemTypes.MailListRow ||
        mailStore.typeOfItemBeingDragged === DraggableItemTypes.MultiMailListConversationRows ||
        mailStore.typeOfItemBeingDragged === DraggableItemTypes.MultiMailListMessageRows ||
        mailStore.typeOfItemBeingDragged === DraggableItemTypes.LocalFile ||
        mailStore.typeOfItemBeingDragged === DraggableItemTypes.Attachment ||
        mailStore.typeOfItemBeingDragged === DraggableItemTypes.NoteFeedItem
    );
}
